import { FaFacebookF, FaLinkedinIn } from "react-icons/fa";
import { HiOutlineMail } from "react-icons/hi";
import {
  FiPhone,
  FiMail,
  FiSend,
  FiUser,
  FiMessageSquare,
} from "react-icons/fi";
import React from "react";
import { motion } from "framer-motion";
import { Helmet } from "react-helmet-async";
import { useAppDispatch, useAppSelector } from "../../store/hooks";
import { sendQueryEmails } from "../../store/admin/emailSlice";
import { useToast } from "../../components/utilities/Toast";

const inputClassName =
  "w-full rounded-xl bg-black/40 border border-white/10 px-4 py-3.5 text-white placeholder:text-gray-500 outline-none transition-all focus:border-[#2fa9bf] focus:ring-2 focus:ring-[#2fa9bf]/30";

const cards = [
  {
    title: "Call Us",
    data1: "+44 788 234 7988",
    data2: "+1 701 738 1847",
    icon: <FiPhone />,
  },
  {
    title: "Learn More",
    data1: "enquiry@toothsketch.com",
    data2: "",
    icon: <FiMail />,
  },
  {
    title: "Customer Support",
    data1: "support@toothsketch.com",
    data2: "",
    icon: <FiSend />,
  },
];

const ContactUs = () => {
  const dispatch = useAppDispatch();
  const { showToast } = useToast();
  const { loading } = useAppSelector((state) => state.emailSender);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    const form = e.currentTarget;
    const formData = new FormData(form);

    try {
      await dispatch(
        sendQueryEmails({
          name: formData.get("name") as string,
          email: formData.get("email") as string,
          note: formData.get("note") as string,
        }),
      ).unwrap();

      showToast("Query sent successfully", "success");
      form.reset();
    } catch {
      showToast("Failed to send query", "error");
    }
  };

  return (
    <>
      <Helmet>
        <title>Contact Us</title>
      </Helmet>
      <div className="h-full bg-black text-white flex flex-col py-12 md:py-[4vw] gap-12 md:gap-[4vw] md:px-[5vw]">
        <h1 className="md:text-[4vw] font-bold text-center md:text-start text-3xl">
          Contact Us
        </h1>

        <div className="w-full px-6 flex justify-center">
          <div className="relative w-full max-w-2xl">
            <div className="absolute -top-24 left-1/2 -translate-x-1/2 h-48 w-full max-w-md rounded-full bg-[#2fa9bf]/20 blur-[100px] pointer-events-none" />

            <motion.div
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, ease: "easeOut" }}
              className="relative rounded-3xl bg-white/5 backdrop-blur-xl border border-white/10 shadow-[0_40px_100px_rgba(0,0,0,0.5)] p-8 md:p-10 overflow-hidden"
            >
              <div className="absolute inset-0 rounded-3xl bg-gradient-to-br from-white/10 via-transparent to-transparent opacity-20 pointer-events-none" />

              <div className="relative z-10 mb-8 text-center md:text-left">
                <h2 className="text-2xl md:text-3xl font-semibold text-white">
                  Send us a message
                </h2>
                <p className="mt-2 text-sm text-gray-400">
                  Have a question or want to work with us? Fill out the form and
                  we&apos;ll get back to you shortly.
                </p>
              </div>

              <form
                onSubmit={handleSubmit}
                className="relative z-10 space-y-5"
              >
                <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                  <div className="space-y-2">
                    <label
                      htmlFor="name"
                      className="block text-xs font-medium text-gray-400 uppercase tracking-wider"
                    >
                      Full Name
                    </label>
                    <div className="relative">
                      <FiUser className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-500 w-4 h-4 pointer-events-none" />
                      <input
                        id="name"
                        type="text"
                        name="name"
                        placeholder="John Doe"
                        required
                        className={`${inputClassName} pl-11`}
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <label
                      htmlFor="email"
                      className="block text-xs font-medium text-gray-400 uppercase tracking-wider"
                    >
                      Email Address
                    </label>
                    <div className="relative">
                      <FiMail className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-500 w-4 h-4 pointer-events-none" />
                      <input
                        id="email"
                        type="email"
                        name="email"
                        placeholder="you@example.com"
                        required
                        className={`${inputClassName} pl-11`}
                      />
                    </div>
                  </div>
                </div>

                <div className="space-y-2">
                  <label
                    htmlFor="note"
                    className="block text-xs font-medium text-gray-400 uppercase tracking-wider"
                  >
                    Your Message
                  </label>
                  <div className="relative">
                    <FiMessageSquare className="absolute left-4 top-4 text-gray-500 w-4 h-4 pointer-events-none" />
                    <textarea
                      id="note"
                      name="note"
                      placeholder="Tell us how we can help..."
                      rows={5}
                      required
                      className={`${inputClassName} resize-none pl-11`}
                    />
                  </div>
                </div>

                <motion.button
                  type="submit"
                  disabled={loading}
                  whileHover={{ scale: loading ? 1 : 1.02 }}
                  whileTap={{ scale: loading ? 1 : 0.98 }}
                  className="relative w-full overflow-hidden rounded-xl bg-gradient-to-r from-[#2fa9bf] to-cyan-400 py-3.5 font-semibold text-black shadow-[0_20px_60px_rgba(47,169,191,0.35)] transition-shadow hover:shadow-[0_20px_60px_rgba(47,169,191,0.5)] disabled:opacity-60 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                >
                  {loading ? (
                    <>
                      <span className="h-4 w-4 animate-spin rounded-full border-2 border-black/30 border-t-black" />
                      Sending...
                    </>
                  ) : (
                    <>
                      <FiSend className="w-4 h-4" />
                      Send Message
                    </>
                  )}
                </motion.button>
              </form>
            </motion.div>
          </div>
        </div>

        <div className="flex flex-col items-center justify-center">
          <div className="flex flex-col md:flex-row items-center justify-center gap-12 md:gap-[7vw]">
            {cards.map((item, index) => (
              <div
                key={index}
                className="flex gap-4 md:gap-[1vw] flex-col w-full h-[50vh] md:w-[22vw] md:h-[16vw] p-4 md:p-[1vw] rounded-xl items-center justify-center bg-white text-black overflow-hidden"
              >
                <div className="w-20 h-20 md:w-[5vw] md:h-[5vw] mb-2 text-white bg-[#2fa9bf] rounded-full p-4">
                  {React.cloneElement(item.icon, {
                    className: "w-full h-full",
                  })}
                </div>
                <h2 className="md:text-[1.5vw] font-bold md:-mt-[0.25vw]">
                  {item.title}
                </h2>
                <p className="md:text-[1.2vw] opacity-70 md:-mt-[0.25vw]">
                  {item.data1.startsWith("+") ? (
                    <a href={`tel:${item.data1}`} className="hover:underline">
                      {item.data1}
                    </a>
                  ) : (
                    <a
                      href={`mailto:${item.data1}`}
                      className="hover:underline"
                    >
                      {item.data1}
                    </a>
                  )}
                </p>
                {item.data2 && (
                  <p className="md:text-[1.2vw] md:-mt-[0.5vw] opacity-70">
                    {item.data2.startsWith("+") ? (
                      <a href={`tel:${item.data2}`} className="hover:underline">
                        {item.data2}
                      </a>
                    ) : (
                      <a
                        href={`mailto:${item.data2}`}
                        className="hover:underline"
                      >
                        {item.data2}
                      </a>
                    )}
                  </p>
                )}
              </div>
            ))}
          </div>

          <div className="mt-16 md:mt-[2vw] flex flex-col items-center gap-6">
            <h2 className="text-xl md:text-[1.6vw] font-semibold">
              Connect With Us
            </h2>

            <div className="flex gap-6 md:gap-[2vw]">
              <a
                href="mailto:enquiry@toothsketch.com"
                className="w-14 h-14 md:w-[3.5vw] md:h-[3.5vw] flex items-center justify-center rounded-full bg-white text-black hover:bg-[#2fa9bf] hover:text-white transition-all duration-300 hover:rotate-[360deg]"
              >
                <HiOutlineMail className="w-6 h-6 md:w-[1.6vw] md:h-[1.6vw]" />
              </a>

              <a
                href="https://www.facebook.com/ToothSketch"
                target="_blank"
                rel="noopener noreferrer"
                className="w-14 h-14 md:w-[3.5vw] md:h-[3.5vw] flex items-center justify-center rounded-full bg-white text-black hover:bg-[#2fa9bf] hover:text-white transition-all duration-300 hover:rotate-[360deg]"
              >
                <FaFacebookF className="w-5 h-5 md:w-[1.4vw] md:h-[1.4vw]" />
              </a>

              <a
                href="https://www.linkedin.com/company/toothsketch"
                target="_blank"
                rel="noopener noreferrer"
                className="w-14 h-14 md:w-[3.5vw] md:h-[3.5vw] flex items-center justify-center rounded-full bg-white text-black hover:bg-[#2fa9bf] hover:text-white transition-all duration-300 hover:rotate-[360deg]"
              >
                <FaLinkedinIn className="w-5 h-5 md:w-[1.4vw] md:h-[1.4vw]" />
              </a>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default ContactUs;
